import React from 'react'
import NextImage from 'next/image'
import { Type, TextBlock } from '@/components/common'
import useHydration from '@/services/hooks/useHydration'
import useMeasure from '@/services/hooks/useMeasure'
import useWindowSize from '@/services/hooks/useWindowSize'
import ApplyButton from '../ApplyButton'
import { CTABlock } from '../types'

export interface Props extends CTABlock {
  className?: string
}

const CaseStudy: React.FC<Props> = ({
  title,
  subtitle,
  text,
  image,
  buttonUrl,
  buttonText,
  className,
}) => {
  const { innerWidth } = useWindowSize()
  const crop = innerWidth < 768
  const [ref, rect] = useMeasure<HTMLDivElement>()
  const { isHydrated } = useHydration()

  const imgSrc = `https:${image?.file.url}`
  const aspectRatio =
    (image?.file.details.image.width || 1) /
    (image?.file.details.image.height || 1)
  const imgHeight = crop ? (768 * 2) / 3 : rect.height
  const imgWidth = crop ? 768 : (rect.width || 1) / aspectRatio

  return (
    <div className={className}>
      <div className="2xl:p-20 flex flex-col justify-center p-6 sm:p-8 md:w-1/2 xl:p-16">
        <Type
          as="h4"
          variant={['title-sm', 'lg:title-md']}
          className="mb-2 text-gray"
        >
          {subtitle}
        </Type>

        <Type
          as="h3"
          variant={['heading-sm', 'lg:heading-md']}
          className="mb-4 xl:mb-8"
        >
          {title}
        </Type>

        <TextBlock document={text} />

        <ApplyButton
          url={buttonUrl}
          text={buttonText}
          className="mt-4 xl:mt-8"
        />
      </div>

      <div ref={ref} className="flex md:w-1/2">
        {isHydrated && imgWidth && imgHeight && (
          <NextImage
            alt={image?.description || title}
            src={imgSrc}
            width={imgWidth}
            height={imgHeight}
            className="w-full object-cover"
            loading="lazy"
          />
        )}
      </div>
    </div>
  )
}

export default CaseStudy
